import React, { memo } from 'react'
import { Box, Text, Flex } from '@chakra-ui/core'
import { Task } from 'constants/interfaces'
import { Post } from 'redux/reducers/feed'
import { TaskV2ListItem } from 'components/Tasks/TaskV2ListItem'
import { PostWrapper } from 'components/Post'
import colors from 'constants/colors'
import { PartyPopper } from 'lucide-react'
import { SectionHeader } from './SectionHeader'
import { OwnerTasksSection } from './OwnerTasksSection'
import useLocaleText from 'components/useLocaleText'

interface AllItemsViewProps {
  tasks: Task[]
  posts: Post[]
  userId: string
  readPosts: Post[]
  ownerTasks?: Task[]
  isSelf?: boolean
  onOwnerTaskClick?: (task: Task) => void
  handleOpenSelectedPost: (postID: string) => void
  selectedTaskID?: string | null
  onTaskSelect?: (tid: string) => void
  onTaskClose?: () => void
}

export const AllItemsView = memo(
  ({
    tasks,
    posts,
    readPosts,
    ownerTasks = [],
    isSelf = false,
    onOwnerTaskClick,
    userId,
    handleOpenSelectedPost,
    selectedTaskID,
    onTaskSelect,
    onTaskClose,
  }: AllItemsViewProps) => {
    const all_catched_up = useLocaleText('all_catched_up')
    const urg_tasks = useLocaleText('urg_tasks')
    const unread_posts = useLocaleText('unread_posts')
    const t_posts = useLocaleText('t_all_msgs_btn')
    const t_created_by_me = useLocaleText('t_created_by_me')
    const my_posts = useLocaleText('my_posts')

    const hasNoTasksAndPosts = tasks.length === 0 && posts.length === 0 && !(isSelf && ownerTasks.length > 0)

    return (
      <Flex flexDirection="column" height="100%">
        {isSelf && ownerTasks.length > 0 && (
          <Box flexShrink={0}>
            <SectionHeader title={t_created_by_me} />
            <OwnerTasksSection tasks={ownerTasks} onTaskClick={onOwnerTaskClick} />
          </Box>
        )}

        {tasks.length > 0 && (
          <Box flexShrink={0}>
            <SectionHeader title={urg_tasks} />
            {tasks.map((t) => (
              <TaskV2ListItem
                key={t.tid}
                task={t}
                uid={userId}
                isPersonalView={true}
                selectedTaskID={selectedTaskID}
                onTaskSelect={onTaskSelect}
                onTaskClose={onTaskClose}
              />
            ))}
          </Box>
        )}

        {posts.length > 0 && (
          <Box flexShrink={0}>
            <SectionHeader title={isSelf ? my_posts : unread_posts} />
            {posts.map((p) => (
              <PostWrapper
                key={p.post_id}
                isAllPostCollapsed={true}
                posts={[]}
                {...p}
                handleOpenSelectedPost={handleOpenSelectedPost}
                isPersonalViewMode={true}
              />
            ))}
          </Box>
        )}

        {readPosts.length > 0 && !hasNoTasksAndPosts && (
          <Box flexShrink={0}>
            <SectionHeader title={t_posts} />
            {readPosts.map((p) => (
              <PostWrapper
                key={p.post_id}
                isAllPostCollapsed={true}
                posts={[]}
                {...p}
                handleOpenSelectedPost={handleOpenSelectedPost}
                isPersonalViewMode={true}
              />
            ))}
          </Box>
        )}

        {hasNoTasksAndPosts && (
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py="60px"
            px="20px"
            style={{ rowGap: 16 }}
            flexShrink={0}
          >
            <PartyPopper size={68} color={colors.greyDark2} strokeWidth={1.5} />
            <Box textAlign="center">
              <Text fontSize="18px" fontWeight="bold" color={colors.blackLight} mb="8px">
                {all_catched_up}
              </Text>
            </Box>
          </Flex>
        )}

        {readPosts.length > 0 && hasNoTasksAndPosts && (
          <Box flex="1" minHeight="0">
            <SectionHeader title={t_posts} />
            {readPosts.map((p) => (
              <PostWrapper
                key={p.post_id}
                isAllPostCollapsed={true}
                posts={[]}
                {...p}
                handleOpenSelectedPost={handleOpenSelectedPost}
                isPersonalViewMode={true}
              />
            ))}
          </Box>
        )}
      </Flex>
    )
  }
)
